import type Pet from '~models/pet'
import Sentry from '~utils/sentry'
import {
  getIntakeSpecV1,
  getIntakeSpecV2,
  type AnyIntakeSpec,
  type IntakeQuestion,
} from '~repositories/hooks/use-intake-spec'

import { loadedData } from './data'
import {
  ageWeeksOf,
  getHighestAlertHit,
  INTAKE_UNKNOWN,
  isIntakeSpecV2,
  resolveIntake,
  sexOfGender,
  speciesOfKind,
  type IntakeAnswer,
  type IntakeAlertLevel,
  type IntakeAnswers,
  type IntakeEvalErrorHandler,
  type IntakeQuestionV2,
  type IntakeSpecV2,
} from './intake-v2'
import type { ConsultationData } from './types'

export type SafetyAnswers = IntakeAnswers

// 화면에서 뺐지만 예전에 저장된 임시 답에 남아 있을 수 있는 키 (제출 전에 걷어 낸다)
const REMOVED_PAGES: Record<string, string[]> = {
  diarrhea: ['color'],
  vomiting: ['color', 'frequency'],
}
const REMOVED_COMMON_PAGES = ['experienced_medical_conditions']

/** 증상별 기존 문진 화면 (공통 이력 화면 제외). 순서는 data.ts 선언 순서 */
export const getLegacyPages = (concern: string) => {
  const common = getCommonPages()
  return Object.keys(loadedData.checkup[concern] ?? {}).filter(page => !common.includes(page))
}

/** 안전 질문 뒤에 이어지는 공통 이력 화면 (period → vaccine → prevention) */
export const getCommonPages = () => Object.keys(loadedData.common_checkup)

/** 신규 증상(others.category)이면 그 분류의 모듈을 쓴다 */
export const getModuleKey = (concern: string, category?: string) =>
  concern === 'others' && category ? category : concern

export const getAllSafetyQuestions = (
  spec: AnyIntakeSpec | undefined,
  moduleKey: string
): IntakeQuestion[] => {
  const v1 = getIntakeSpecV1(spec)
  return [...(v1?.modules?.[moduleKey] ?? []), ...(v1?.safety ?? [])]
}

/** 앞 질문의 답이 skip_if 에 해당하면 묻지 않는다 (서버 is_skipped 와 같은 조건: 단일 선택 값만 비교) */
const isSkippedQuestion = (question: IntakeQuestion, answers: SafetyAnswers) =>
  Object.entries(question.skip_if ?? {}).some(([id, values]) => {
    const answer = answers[id]
    return typeof answer === 'string' && values.includes(answer)
  })

/**
 * 묻지 않게 된 질문의 남은 답을 지우고(answers 를 직접 수정), 실제로 묻는 질문 목록을 돌려준다.
 * 질문 순서대로 보므로 건너뛴 질문의 답에 기대는 skip_if 도 함께 풀린다.
 */
export const pruneSkippedAnswers = (all: IntakeQuestion[], answers: SafetyAnswers) =>
  all.filter(question => {
    if (!isSkippedQuestion(question, answers)) return true
    delete answers[question.id]
    return false
  })

/** 현재 답 기준으로 실제 묻는 안전 질문 목록 (스텝 번호는 이 목록의 인덱스) */
export const getSafetyQuestions = (all: IntakeQuestion[], answers: SafetyAnswers) =>
  pruneSkippedAnswers(all, { ...answers })

// ---------------------------------------------------------------------------------------------
// 문진 v2 (DRT-7186): 증상 선택 → flows[증상] 한 질문씩(show_if 로 거름) → 상세 입력/제출
// ---------------------------------------------------------------------------------------------

type PetForIntake =
  | Pick<Pet.ILevel1, 'kind' | 'born_at' | 'gender' | 'is_neutered'>
  | null
  | undefined

/** show_if 의 종·나이·성별·중성화 문맥 (펫 정보를 모르면 null) */
export const getPetContext = (spec: IntakeSpecV2, pet: PetForIntake) => ({
  species: speciesOfKind(spec, pet?.kind),
  ageWeeks: ageWeeksOf(pet?.born_at, spec.unknown_born_before),
  sex: sexOfGender(spec, pet?.gender),
  neutered: pet?.is_neutered ?? null,
})

/**
 * 이 상담이 쓰는 질문지. 증상 화면에서 Next 를 누를 때 정한 문진 버전(intake_version)을 따른다:
 * v1 으로 시작한 상담은 질문지가 v2 로 바뀌어도 v1 으로 끝낸다. 정하기 전에는 받은 질문지 그대로
 */
export const getConsultationIntakeSpec = (
  spec: AnyIntakeSpec | undefined,
  pinnedVersion: number | null | undefined
): AnyIntakeSpec | undefined => (pinnedVersion === 1 && isIntakeSpecV2(spec) ? undefined : spec)

// 질문지의 조건 문법을 이 앱이 모를 때: 화면은 계속 보여 주고(fail-open) Sentry 에 한 번만 알린다
const reportedEvalErrors = new Set<string>()
const reportEvalError: IntakeEvalErrorHandler = (error, questionId, where) => {
  const key = `${where}:${questionId}`
  if (reportedEvalErrors.has(key)) return
  reportedEvalErrors.add(key)
  Sentry.captureException(error, { extra: { questionId, where, feature: 'intake_v2' } })
}

/** 질문지의 보기·형식에 맞는 답인지 (예전 임시 답·잘못된 값이 조건을 열거나 서버 400 을 내지 않게) */
const isValidAnswerV2 = (question: IntakeQuestionV2 | undefined, value: IntakeAnswer) => {
  if (!question) return false
  if (value === INTAKE_UNKNOWN) return question.unknown
  const choices = question.options.map(option => option.value)
  switch (question.type) {
    case 'single':
      return typeof value === 'string' && choices.includes(value)
    case 'multi':
      return (
        Array.isArray(value) &&
        value.length > 0 &&
        value.every(v => choices.includes(v) || (v === INTAKE_UNKNOWN && question.unknown))
      )
    case 'number':
      return (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        value >= (question.min ?? 0) &&
        value <= (question.max ?? Infinity)
      )
    case 'text':
      return typeof value === 'string' && value.length > 0
  }
}

/**
 * 다중 선택 답에서 함께 고를 수 없는 값(exclusive·'unknown'·empty_means)을 정리한다: 다른 보기와 섞여 있으면 빼고,
 * 그런 값만 여러 개면 첫 값만 남긴다. exclusive_groups 는 묶음마다 첫 값만 남긴다.
 * 화면은 섞이지 않게 고르지만 예전 임시 답 등으로 ['none', 'x'] 가 가지 않게
 */
export const normalizeMultiAnswersV2 = (spec: IntakeSpecV2, answers: SafetyAnswers) =>
  Object.fromEntries(
    Object.entries(answers).map(([id, value]) => {
      const question = spec.questions[id]
      if (question?.type !== 'multi' || !Array.isArray(value)) return [id, value]
      const exclusive = new Set<string>([
        ...question.exclusive,
        INTAKE_UNKNOWN,
        ...(question.empty_means ? [question.empty_means] : []),
      ])
      const groups = question.exclusive_groups ?? []
      const regular = value.filter(
        (v, index) =>
          !exclusive.has(v) &&
          !value.slice(0, index).some(prev => groups.some(g => g.includes(v) && g.includes(prev)))
      )
      return [id, regular.length > 0 ? regular : value.slice(0, 1)]
    })
  ) as SafetyAnswers

/** 현재 답 기준으로 보이는 v2 질문 목록과 남는 답 (스텝 번호는 visible 의 인덱스). 잘못된 답은 먼저 거른다 */
export const getVisibleQuestionsV2 = (
  spec: IntakeSpecV2,
  flowKey: string,
  answers: SafetyAnswers,
  pet: PetForIntake
) => {
  const { species, ageWeeks, sex, neutered } = getPetContext(spec, pet)
  const valid = Object.fromEntries(
    Object.entries(answers).filter(([id, value]) => isValidAnswerV2(spec.questions[id], value))
  )
  return resolveIntake(spec, flowKey, valid, species, ageWeeks, sex, neutered, reportEvalError)
}

/** 지금 답으로 띄울 가장 높은 응급 안내 (흐름 전체의 규칙, 보이는 질문의 답만으로 평가) */
export const getAlertHitV2 = (
  spec: IntakeSpecV2,
  flowKey: string,
  answers: SafetyAnswers,
  pet: PetForIntake
) => {
  const { kept } = getVisibleQuestionsV2(spec, flowKey, answers, pet)
  return getHighestAlertHit(
    spec,
    { concern: flowKey, answers: kept, ...getPetContext(spec, pet) },
    reportEvalError
  )
}

/**
 * 숨겨진 질문(앞 답이 바뀌어 안 묻게 된 질문)과 흐름 밖 질문의 답을 지우고, 숨긴 보기(option show_if)의 값을 뺀다
 * (answers 를 직접 수정)
 */
export const pruneHiddenAnswersV2 = (
  spec: IntakeSpecV2,
  flowKey: string,
  answers: SafetyAnswers,
  pet: PetForIntake
) => {
  const { visible, kept } = getVisibleQuestionsV2(spec, flowKey, answers, pet)
  for (const id of Object.keys(answers)) {
    if (id in kept) answers[id] = kept[id]
    else delete answers[id]
  }
  return visible
}

/**
 * v2 제출 payload 의 checkup: 고른 증상 키에만 { intake_version: 2, category?, intake_alert_shown?, safety } 를 넣는다.
 * v1 필드(period, vaccination, ...)는 보내지 않고, 숨겨진 질문의 답도 뺀다.
 * alertShown: 이 문진에서 실제로 띄운 가장 높은 응급 안내 단계. 뒤에서 조건이 사라져도 서버가 더 높은 쪽을 쓴다
 */
export const buildCheckupV2 = (
  checkup: NonNullable<ConsultationData['checkup']>,
  spec: IntakeSpecV2,
  pet: PetForIntake,
  alertShown?: IntakeAlertLevel | null
): NonNullable<ConsultationData['checkup']> => {
  const entries = Object.entries(checkup as Record<string, Record<string, unknown> | null>)
  const concern = entries.find(([, value]) => value !== null)?.[0]
  const result: Record<string, unknown> = Object.fromEntries(entries.map(([key]) => [key, null]))
  if (!concern) return result as NonNullable<ConsultationData['checkup']>

  const data = checkup[concern] as Record<string, unknown>
  const category = concern === 'others' ? (data.category as string | undefined) : undefined
  // 공백만 있는 텍스트는 답이 아니다 (입력 중에는 그대로 두고 제출할 때만 뺀다). 숨김 계산 전에 거른다
  // 다중 선택의 '해당 없음'·'모름'이 다른 보기와 섞인 답은 정리한다
  const answers = normalizeMultiAnswersV2(
    spec,
    Object.fromEntries(
      Object.entries((data.safety as SafetyAnswers | undefined) ?? {}).filter(
        ([, value]) => typeof value !== 'string' || value.trim() !== ''
      )
    )
  )
  const { kept: safety } = getVisibleQuestionsV2(
    spec,
    getModuleKey(concern, category),
    answers,
    pet
  )
  // 서버는 'urgent' | 'emergency' 외의 값을 거부한다: 띄운 적이 없으면 키를 보내지 않는다
  const shown = alertShown === 'urgent' || alertShown === 'emergency' ? alertShown : undefined
  result[concern] = {
    intake_version: 2,
    ...(category ? { category } : {}),
    ...(shown ? { intake_alert_shown: shown } : {}),
    safety,
  }
  return result as NonNullable<ConsultationData['checkup']>
}

interface ScreensParams {
  concern: string
  category?: string
  spec?: AnyIntakeSpec
  answers?: SafetyAnswers
  pet?: PetForIntake
}

/**
 * 선택한 증상 경로의 화면 구성과 전체 화면 수.
 * 순서: 주요 증상(1) → 증상별 기존 문진 화면 → 증상 모듈 + 안전 질문(skip_if 에 걸린 것 제외)
 *       → 공통 이력(period, vaccine, prevention) → 상세 입력/제출(1)
 */
export const getConsultationScreens = ({
  concern,
  category,
  spec,
  answers = {},
  pet,
}: ScreensParams) => {
  // v2: 증상 선택(1) → 보이는 질문 → 상세 입력/제출(1). 기존 증상 화면·공통 이력 화면은 쓰지 않는다
  const specV2 = getIntakeSpecV2(spec)
  if (specV2) {
    const { visible } = getVisibleQuestionsV2(specV2, getModuleKey(concern, category), answers, pet)
    return {
      legacyPages: [] as string[],
      safetyQuestions: visible as ReadonlyArray<{ id: string }>,
      commonPages: [] as string[],
      total: 1 + visible.length + 1,
    }
  }
  const legacyPages = getLegacyPages(concern)
  const commonPages = getCommonPages()
  const safetyQuestions = getSafetyQuestions(
    getAllSafetyQuestions(spec, getModuleKey(concern, category)),
    answers
  )
  return {
    legacyPages,
    safetyQuestions: safetyQuestions as ReadonlyArray<{ id: string }>,
    commonPages,
    total: 1 + legacyPages.length + safetyQuestions.length + commonPages.length + 1,
  }
}

export type ConsultationScreens = ReturnType<typeof getConsultationScreens>

// 진행 바의 현재 위치 (1부터). 주요 증상 선택 = 1, 상세 입력/제출 = total
export const MAIN_CONCERN_SCREEN = 1
export const getLegacyScreenNumber = (screens: ConsultationScreens, page: string) =>
  2 + Math.max(screens.legacyPages.indexOf(page), 0)
export const getSafetyScreenNumber = (screens: ConsultationScreens, step: number) =>
  2 + screens.legacyPages.length + step
export const getCommonScreenNumber = (screens: ConsultationScreens, page: string) =>
  2 +
  screens.legacyPages.length +
  screens.safetyQuestions.length +
  Math.max(screens.commonPages.indexOf(page), 0)

/**
 * 제출 직전 정리: 예전 임시 답에 남은 삭제된 화면의 키와, 건너뛰게 된 안전 질문의 답을 뺀다.
 * (서버 시리얼라이저는 color 같은 키가 null 로 오면 거부하므로 키 자체를 보내지 않는다)
 */
export const sanitizeCheckup = (
  checkup: NonNullable<ConsultationData['checkup']>,
  spec?: AnyIntakeSpec,
  pet?: PetForIntake,
  alertShown?: IntakeAlertLevel | null
): NonNullable<ConsultationData['checkup']> => {
  const specV2 = getIntakeSpecV2(spec)
  if (specV2) return buildCheckupV2(checkup, specV2, pet, alertShown)

  const cleaned = JSON.parse(JSON.stringify(checkup)) as Record<string, Record<string, unknown>>
  const category = cleaned.others?.category as string | undefined

  for (const [concern, data] of Object.entries(cleaned)) {
    if (!data) continue
    for (const key of [...REMOVED_COMMON_PAGES, ...(REMOVED_PAGES[concern] ?? [])]) {
      delete data[key]
    }
    if (data.safety && typeof data.safety === 'object') {
      pruneSkippedAnswers(
        getAllSafetyQuestions(spec, getModuleKey(concern, category)),
        data.safety as SafetyAnswers
      )
    }
  }
  return cleaned as NonNullable<ConsultationData['checkup']>
}
